import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Sector
} from 'recharts'
import { ChartTooltip } from './ChartTooltip'
import { CATEGORY_PALETTE, EASING, STAGGER_DELAY } from './chart-tokens'

/**
 * Donut chart com:
 * - Valor total no centro
 * - Slice destacado no hover (sai do círculo + glow)
 * - Lista lateral de categorias com hover sincronizado
 * - Animação de rotação dos slices na entrada
 */
function ActiveSlice(props) {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 6}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={outerRadius + 8}
        outerRadius={outerRadius + 10}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        opacity={0.35}
      />
    </g>
  )
}

export function CategoryDonutChart({
  data,
  formatCurrency,
  innerRadius = 70,
  outerRadius = 100,
  centerLabel,
  centerValue
}) {
  const [activeIndex, setActiveIndex] = useState(undefined)

  const total = data.reduce((sum, d) => sum + (Number(d.valor) || 0), 0)

  return (
    <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-center">
      <div className="relative">
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              dataKey="valor"
              paddingAngle={2}
              stroke="#fff"
              strokeWidth={2}
              activeIndex={activeIndex}
              activeShape={ActiveSlice}
              onMouseEnter={(_, idx) => setActiveIndex(idx)}
              onMouseLeave={() => setActiveIndex(undefined)}
              isAnimationActive
              animationDuration={1000}
              animationEasing="ease-out"
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={CATEGORY_PALETTE[index % CATEGORY_PALETTE.length]}
                />
              ))}
            </Pie>
            <Tooltip
              content={<ChartTooltip formatter={v => formatCurrency(v)} />}
              position={{ y: 0 }}
              wrapperStyle={{ zIndex: 1000 }}
            />
          </PieChart>
        </ResponsiveContainer>

        {/* Valor no centro */}
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
          <span className="text-xs font-medium uppercase tracking-wider text-slate-500">
            {centerLabel}
          </span>
          <span className="text-xl font-bold text-slate-900">
            {centerValue ?? formatCurrency(total)}
          </span>
        </div>
      </div>

      <div className="grid gap-2 md:max-w-[15rem]">
        {data.map((entry, index) => {
          const color = CATEGORY_PALETTE[index % CATEGORY_PALETTE.length]
          const isActive = activeIndex === index
          const pct = total > 0 ? ((entry.valor / total) * 100).toFixed(0) : 0
          return (
            <motion.button
              type="button"
              key={entry.nome}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(undefined)}
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.4,
                delay: 0.4 + index * STAGGER_DELAY,
                ease: EASING.smoothOut
              }}
              className={`flex items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors ${
                isActive ? 'bg-slate-100' : 'bg-slate-50 hover:bg-slate-100'
              }`}
            >
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: color }}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-slate-700">
                  {entry.nome}
                </p>
                <p className="text-xs text-slate-500">
                  {formatCurrency(entry.valor)}
                </p>
              </div>
              <span className="text-xs font-semibold text-slate-400">
                {pct}%
              </span>
            </motion.button>
          )
        })}
      </div>
    </div>
  )
}

export default CategoryDonutChart
